import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getTiers, addTier, updateTier, deleteTier } from '../../actions/tierActions';
import { getPrograms } from '../../actions/programActions';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { X } from 'lucide-react';

const PRESET_COLORS = ['#1086a4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#054581', '#6366f1'];

const TiersTab = ({
  tier: { tiers, loading },
  program: { programs },
  getTiers,
  addTier,
  updateTier,
  deleteTier,
  getPrograms
}) => {
  const [selectedProgram, setSelectedProgram] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ name: '', xpRequired: '', color: '#1086a4' });

  useEffect(() => {
    getPrograms();
  }, [getPrograms]);

  // Auto-select first program
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      setSelectedProgram(programs[0]._id);
    }
  }, [programs, selectedProgram]);

  // Fetch tiers when program changes
  useEffect(() => {
    if (selectedProgram) {
      getTiers(selectedProgram);
    }
  }, [selectedProgram, getTiers]);

  const resetForm = () => {
    setForm({ name: '', xpRequired: '', color: '#1086a4' });
    setEditingTier(null);
    setShowForm(false);
  };

  const handleEdit = (tier) => {
    setForm({
      name: tier.name,
      xpRequired: (tier.xpRequired || 0).toString(),
      color: tier.color || '#1086a4'
    });
    setEditingTier(tier);
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      program: selectedProgram,
      name: form.name,
      xpRequired: Number(form.xpRequired),
      color: form.color
    };

    if (editingTier) {
      updateTier(editingTier._id, data);
    } else {
      addTier(data);
    }
    resetForm();
  };

  const handleDelete = (id) => {
    deleteTier(id);
    setShowDeleteConfirm(null);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#054581]">Tiers</h2>
          <p className="text-sm text-gray-500 mt-1">
            Define XP tiers per program. Ambassadors reach a tier when their XP meets the threshold.
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          disabled={!selectedProgram}
          className="flex items-center gap-2 px-4 py-2 bg-[#1086a4] text-white rounded-lg font-medium hover:bg-[#0d7a94] transition-colors disabled:opacity-50"
        >
          <FaPlus size={12} />
          Add Tier
        </button>
      </div>

      {/* Program Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Program</label>
        <select
          value={selectedProgram}
          onChange={e => setSelectedProgram(e.target.value)}
          className="w-full max-w-xs px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
        >
          <option value="">Select a program</option>
          {programs.map(p => (
            <option key={p._id} value={p._id}>{p.title}</option>
          ))}
        </select>
      </div>

      {/* Tiers List */}
      {!selectedProgram ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-sm">Select a program to manage its tiers.</p>
        </div>
      ) : loading ? (
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-16 bg-gray-200 rounded-lg" />)}
        </div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg font-medium mb-1">No tiers configured</p>
          <p className="text-sm">Add your first tier for this program.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tiers.map(tier => (
            <div
              key={tier._id}
              className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-3 h-10 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tier.color || '#1086a4' }}
                />
                <div>
                  <h3 className="font-semibold text-[#1e2d40]">{tier.name}</h3>
                  <p className="text-sm text-gray-500">
                    {(tier.xpRequired || 0).toLocaleString()} XP required
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleEdit(tier)}
                  className="p-2 text-gray-400 hover:text-[#1086a4] transition-colors"
                >
                  <FaEdit size={14} />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(tier._id)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <FaTrash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-[#054581]">
                {editingTier ? 'Edit Tier' : 'Add Tier'}
              </h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Novice"
                  className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">XP Required</label>
                <input
                  type="number"
                  value={form.xpRequired}
                  onChange={e => setForm({ ...form, xpRequired: e.target.value })}
                  placeholder="e.g. 100"
                  className="w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                  min="0"
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Ambassadors reach this tier when they earn this many XP or more.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <input
                    type="color"
                    value={form.color}
                    onChange={e => setForm({ ...form, color: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 border border-[#1086a4] rounded-lg text-[#1086a4] font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#1086a4] text-white rounded-lg font-medium hover:bg-[#0d7a94] transition-colors"
                >
                  {editingTier ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-[#054581] mb-2">Delete Tier</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this tier? This action cannot be undone.</p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  tier: state.tier,
  program: state.program
});

export default connect(mapStateToProps, { getTiers, addTier, updateTier, deleteTier, getPrograms })(TiersTab);
